import { useRef, useEffect } from "react";
import "./InputDescriptionStaticHeight.css";

type InputDescriptionStaticHeightProps = {
  onChangeText?: (text: string) => void;
  initialText?: string | null;
};

export function InputDescriptionStaticHeight({ onChangeText, initialText }: InputDescriptionStaticHeightProps) {
  const ref = useRef<HTMLDivElement>(null);
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  const handleInput = () => {
    const el = ref.current;
    if (!el) return;

    if (el.innerText.trim() === "" || el.innerHTML === "<br>") {
      el.innerHTML = "";
    }

    if (onChangeText) {
      onChangeText(el.innerText);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");

    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    selection.deleteFromDocument();
    const range = selection.getRangeAt(0);

    const frag = document.createDocumentFragment();
    const parts = text.split(urlRegex);

    for (const part of parts) {
      if (urlRegex.test(part)) {
        const link = document.createElement("a");
        link.href = part;
        link.textContent = part;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.className = "text-blue-600 underline";
        frag.appendChild(link);
      } else {
        frag.appendChild(document.createTextNode(part));
      }
    }

    range.insertNode(frag);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    handleInput(); // Also triggers the callback
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();

      const selection = window.getSelection();
      if (!selection?.rangeCount) return;

      const range = selection.getRangeAt(0);
      const br = document.createElement("br");
      range.insertNode(br);
      range.setStartAfter(br);
      range.collapse(true);

      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  useEffect(() => {
    if (ref.current && initialText) {
      ref.current.innerText = initialText;
    }
    handleInput(); // Trigger change once with initial value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialText]);

  return (
    <div
      ref={ref}
      contentEditable
      onInput={handleInput}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      data-placeholder="What's on your mind?"
      className="post-input w-full flex-grow px-4 py-2 text-sm rounded-xl border border-1 outline-none resize-none overflow-auto leading-relaxed whitespace-pre-wrap break-all"
    />
  );
}

